import {
	type PlacementMessageFrontendFactory,
	type AnyProps,
	type NoProps,
	type PlacementRenderer,
	type AnyFrontendStage,
	type PlacementBrowserConfig,
} from '@post-office/message-maker-browser/types';

import { createPlacementFrontend } from '@post-office/message-maker-browser/create-placement-frontend';

import Badge from '@atlaskit/badge/badge';

import Heading from '@atlaskit/heading/heading';

import Lozenge from '@atlaskit/lozenge/lozenge';

import { Inline } from '@atlaskit/primitives/compiled/inline';

import { Stack } from '@atlaskit/primitives/compiled/stack';

import {
	usePlacementGraphQLSubscriptionApi,
	getSubscriptionMessages,
} from '@post-office/use-placement-api/graphql-subscription';

import { FormattedMessage } from 'react-intl';

const LIVE_NUDGE_DEMO_MESSAGE_DATA_FRAGMENT = `... on PostOfficeMessageLiveNudgeDemoLiveNudgeDemoMessageData {
	__typename
	title
	description
}`;

type PostOfficeMessageLiveNudgeDemoLiveNudgeDemoMessageData = {
	__typename: 'PostOfficeMessageLiveNudgeDemoLiveNudgeDemoMessageData';
	title: string;
	description: string;
};

type SubscriptionMessageData = PostOfficeMessageLiveNudgeDemoLiveNudgeDemoMessageData;

const placementConfig: PlacementBrowserConfig = {
	placementId: 'live-nudge-demo',
	options: {
		choreographer: {
			isDefaultEnabled: false,
		},
		hideInIsolatedCloud: false,
		subscription: {
			enabled: true,
		},
	},
};

const placement = { createFrontend: createPlacementFrontend(placementConfig) };

export const createFrontend: <U extends AnyProps = NoProps>(
	placementRenderer: PlacementRenderer<
		{
			render: AnyFrontendStage;
		},
		U
	>,
) => PlacementMessageFrontendFactory<
	{
		render: AnyFrontendStage;
	},
	U
> = placement.createFrontend;

export const PlacementFrontend: PlacementMessageFrontendFactory<
	{
		render: AnyFrontendStage;
	},
	NoProps
> = createFrontend((Messages) => () => {
	const {
		data: subscriptionData,
		error: subscriptionError,
		isLoading: subscriptionLoading,
	} = usePlacementGraphQLSubscriptionApi<SubscriptionMessageData>({
		dataFragments: [LIVE_NUDGE_DEMO_MESSAGE_DATA_FRAGMENT],
	});

	if (subscriptionError) {
		throw subscriptionError;
	}

	const liveMessages = getSubscriptionMessages<SubscriptionMessageData>(subscriptionData);

	return (
		<Stack space="space.150">
			<Heading as={'h1'} size="medium">
				<FormattedMessage
					id="post-office.live-nudge-demo.main-heading.no-translate"
					defaultMessage="Live Nudge Demo Placement"
					description="Heading for the live nudge demo placement"
				/>
			</Heading>

			<Inline space="space.100" alignBlock="center">
				{subscriptionLoading && (
					<Lozenge appearance="inprogress">
						<FormattedMessage
							id="post-office.live-nudge-demo.connecting.no-translate"
							defaultMessage="Connecting..."
							description="Loading state when connecting to live subscription"
						/>
					</Lozenge>
				)}
				{subscriptionError && (
					<Lozenge appearance="removed">
						<FormattedMessage
							id="post-office.live-nudge-demo.subscription-error.no-translate"
							defaultMessage="Subscription Error!"
							description="Error state when subscription fails"
						/>
					</Lozenge>
				)}
				{liveMessages.length > 0 && (
					<Badge appearance="primary">
						<FormattedMessage
							id="post-office.live-nudge-demo.live-count.no-translate"
							defaultMessage="{count} live"
							description="Badge showing number of live messages"
							values={{ count: liveMessages.length }}
						/>
					</Badge>
				)}
			</Inline>

			{liveMessages.length > 0 && (
				<Stack as="ul" space="space.200">
					{liveMessages.map(({ data: messageData, ...msg }) => {
						return (
							<Inline as="li" key={msg.messageTemplateId}>
								{messageData ? (
									<Messages.render {...msg} {...messageData} />
								) : (
									<Messages.render
										{...msg}
										title="[Temporary] Live message without enrichment"
										description={`Live message template ID: ${msg.messageTemplateId}`}
									/>
								)}
							</Inline>
						);
					})}
				</Stack>
			)}
		</Stack>
	);
});
